/* eslint-disable no-restricted-globals */

import { getMeristemContext, getRouteId } from "src/utils/meristemContext";
import getStore from "src/utils/redux/store";
import { captureException } from "src/utils/error";
import { trackTask } from "src/utils/monitoring/events/tasks";
import {
  isEligibleForBiomarkersCrossSellMed,
  isEligibleForMhtPostCheckoutBuyflow,
  isInAppNativePurchaseEligible,
  isSelectedPlanEligibleForMhtPostCheckoutBuyflow,
  isShowMetabolicForMenCrossSellMedBeforeBiomarkersTestKit,
  isShowMetabolicForMenCrossSellMedReplaceBiomarkersTestKit,
  isShowMetabolicForWomenCrossSellMedBeforeBiomarkersTestKit,
  isShowMetabolicForWomenCrossSellMedReplaceBiomarkersTestKit,
  isStateEligibleForMhtPostCheckoutBuyflow,
  shouldRedirectToPostCheckoutContext,
  userHasPurchasedNoomClinical,
} from "src/utils/userSegment/features";
import goto from ".";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
  pageSetPushReroll,
  pushReroll,
} from "./utils";
import {
  legacyPurchaseRoot,
  setRecommendedPlanById,
  hrtPaymentRoot,
} from "../../utils/redux/slices/recommendedPlan";
import {
  getAndroidMessageHandlers,
  getIOSMessageHandlers,
  NativePurchaseInfo,
} from "src/utils/in-app/messageHandlers";
import { isB2BUpsellClinicalRoute, isHM } from "src/utils/userSegment";
import { getTelehealthPlanId } from "@utils/api/telehealth";
import { getTierByPlanId } from "@utils/plans";
import { GotoEnrollmentParams } from "./enrollment";
import { getPromoCodeState } from "src/utils/redux/slices/promoCode";
import { getFeatureFlagValue } from "../featureFlags";
import { updateForwardedParams } from "src/utils/urlParams";
import { updateSessionState } from "src/pageDefinitions/session";
import { setNoomSessionStorage } from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";

// "/purchase/:planId"
// "/purchase/(en|es|de)/:planId"
// "/purchase-hm/:planId"
function isPurchase() {
  return ["payment", "payment-survey", "payment-checkout"].includes(
    getMeristemContext().context_type
  );
}

export default {
  async purchaseSurvey(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    goto.layer("payment-survey");
  },

  async chooseTrialPrice(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    pageSetPushReroll("/payment/chooseTrialPrice");
  },

  payment() {
    pushReroll(`${legacyPurchaseRoot()}#/payment`, isPurchase);
  },

  async checkout(planId?: string, forceReroll = false) {
    if (isB2BUpsellClinicalRoute()) {
      goto.layer("payment-checkout");
      return;
    }

    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    if (isHM()) {
      pageSetPushReroll(`/payment/hm/checkout`);
      return;
    }

    pushReroll(
      `${legacyPurchaseRoot()}#/checkout`,
      () => isPurchase() && !forceReroll
    );
  },

  async hrtCheckout(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendedPlanById(planId));
    }

    pushReroll(hrtPaymentRoot(), isPurchase);
  },

  async telehealthPaymentDetails() {
    const telehealthPlanId = getTelehealthPlanId();
    await getStore().dispatch(
      setRecommendedPlanById(telehealthPlanId, {
        overwriteForced: true,
      })
    );

    goto.checkout();
  },

  // iOS-only: Android does not support native purchase and uses web checkout instead.
  nativePurchaseProduct() {
    const task = trackTask("NativePurchaseProduct");
    try {
      task.start();
      if (!isInAppNativePurchaseEligible()) {
        throw new Error("Not eligible for native purchase");
      }
      const state = getStore().getState();
      const { recommendedPlan, userData } = state;

      const messageHandler = getIOSMessageHandlers()?.buyflowPurchaseProduct;
      if (!messageHandler) {
        throw new Error("No message handler found");
      }

      if (!recommendedPlan.itunes_product_id) {
        throw new Error("No productId set for native purchase");
      }
      const productInfoPayload: NativePurchaseInfo = {
        productData: {
          productId: recommendedPlan.itunes_product_id,
          subscriptionLength: recommendedPlan.billing_cycle_duration,
        },
        programAvailability: {
          curriculum: "HW",
          isAvailable: true,
          language: userData.language,
        },
        tracking: {
          routeId: getRouteId(),
          tier: getTierByPlanId(recommendedPlan.braintree_id),
          productName: recommendedPlan.product_name ?? "",
        },
      };

      messageHandler.postMessage(productInfoPayload);
      task.success();
    } catch (err) {
      task.failed(err);
      throw err;
    }
  },

  nativePurchaseComplete() {
    if (isInsideIOSWebKit()) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseCompleted.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.ios");
      }
    }
    if (isInsideAndroidWebView()) {
      try {
        const messageHandlers = getAndroidMessageHandlers();
        messageHandlers.buyflowPurchaseCompleted();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseComplete: no message handler found");
    return false;
  },

  redirectToPostEnrollment({
    planId,
    paymentMethod,
    name,
    email,
  }: {
    planId?: string;
    paymentMethod?: string;
    name?: string;
    email?: string;
  }) {
    const state = getStore().getState();
    const { checkout } = state;
    const promoCodeState = getPromoCodeState(state);
    const enrollmentParams: GotoEnrollmentParams = {
      planId,
      paymentMethod,
      name,
      email,
    };

    const { promoCodeIsVip } = promoCodeState;
    if (promoCodeIsVip != null && promoCodeIsVip) {
      enrollmentParams.piv = promoCodeIsVip;
    }

    if (checkout.multiUserPlan.toggled) {
      if (getFeatureFlagValue("useBaselinePriceForMultiUserPlan")) {
        updateForwardedParams({ experimentTag: "exr143var1" });
      } else {
        updateForwardedParams({ experimentTag: "exr105var1" });
      }
    }
    updateForwardedParams({ noom_plan_id: null });

    // Persist some params used in goto.navigateFromCheckout below so that the post-checkout pages
    // can use the same function to navigate
    updateSessionState("browser", {
      enrollmentForwardParams: enrollmentParams,
    });

    if (isShowMetabolicForMenCrossSellMedReplaceBiomarkersTestKit()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.layer("offers-shop-metabolic-for-men");
      return;
    }

    if (isShowMetabolicForWomenCrossSellMedReplaceBiomarkersTestKit()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.layer("offers-shop-metabolic-for-women");
      return;
    }

    if (isShowMetabolicForMenCrossSellMedBeforeBiomarkersTestKit()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.layer("offers-shop-metabolic-for-men");
      return;
    }

    if (isShowMetabolicForWomenCrossSellMedBeforeBiomarkersTestKit()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.layer("offers-shop-metabolic-for-women");
      return;
    }

    if (isEligibleForBiomarkersCrossSellMed()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.layer("offers-shop-biomarkers-test-kit");
      return;
    }

    if (userHasPurchasedNoomClinical()) {
      setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      goto.telehealthEnrollmentSetup();
      return;
    }

    if (
      isEligibleForMhtPostCheckoutBuyflow() &&
      isStateEligibleForMhtPostCheckoutBuyflow() &&
      isSelectedPlanEligibleForMhtPostCheckoutBuyflow()
    ) {
      goto.hrtSurvey();
      return;
    }

    if (shouldRedirectToPostCheckoutContext()) {
      goto.layer("payment-post-checkout");
    } else {
      goto.navigateFromCheckout();
    }
  },

  // Handle post-checkout navigation.
  navigateFromCheckout() {
    // If the user is ineligible for noom premium, they'll end up
    // in a failover pageset that will continue to the next eligible context after noom premium
    goto.noomPremium();
  },
};
